import { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import {
  API_URL_TENDERS,
  API_BASE_URL,
  API_URL_TENDER_MANUAL_DEPARTMENTS,
  API_URL_TENDER_DEPARTMENT_STATUS,
  API_URL_TENDER_DETAILS,
  API_URL_EDIT,
} from '../configuration/Configuration';
import LanePricing from './advancing/LanePrices';
import Checks from './advancing/Checks';
import TenderDocuments from './TenderDocuments';
import {
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  OutlinedInput,
  ListItemText,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  TextField,
  Chip,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import axios from 'axios';
import Map from './Map';
import { useAuth } from '../context/AuthContext'; // Import auth context
import { useNavigate, useSearchParams } from 'react-router-dom';
import { exportDepartmentStatusToExcel } from '../configuration/Utils';

const SELECT_ALL_VALUE = '__select_all__';

const AdvanceTender = ({ isViewer: isViewerProp } = {}) => {
  const { isAuth, userType, user, isLoading } = useAuth();
  // isViewer: prefer explicit prop (passed from Nav route), fall back to AuthContext
  const isViewer = isViewerProp ?? userType?.isViewer ?? false;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const lanePricingRef = useRef();
  const checksRef = useRef();
  const [manualDepartments, setManualDepartments] = useState([]);
  const [selectedManualDepartments, setSelectedManualDepartments] = useState([]);
  const [allLanesManualDepartments, setAllLanesManualDepartments] = useState([]);
  const [manualDeptLoading, setManualDeptLoading] = useState(false);
  const [manualDeptSaving, setManualDeptSaving] = useState(false);
  const [manualDeptError, setManualDeptError] = useState('');
  const [manualDeptSuccess, setManualDeptSuccess] = useState('');
  const tenderId = searchParams.get('tender_id');

  // Department status popup
  const [deptStatusOpen, setDeptStatusOpen] = useState(false);
  const [deptStatusRows, setDeptStatusRows] = useState([]);
  const [deptStatusLoading, setDeptStatusLoading] = useState(false);
  const [deptStatusError, setDeptStatusError] = useState('');

  // Customer contacts (TM only)
  const [customerContacts, setCustomerContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsError, setContactsError] = useState('');
  const [contactsDialogOpen, setContactsDialogOpen] = useState(false);
  const [contactsEditing, setContactsEditing] = useState([]);
  const [newContactEmail, setNewContactEmail] = useState('');
  const [contactsSaving, setContactsSaving] = useState(false);
  const [contactsSaveError, setContactsSaveError] = useState('');

  const handleOpenDeptStatus = async () => {
    setDeptStatusOpen(true);
    setDeptStatusLoading(true);
    setDeptStatusError('');
    try {
      const res = await axios.get(API_URL_TENDER_DEPARTMENT_STATUS(tenderId), { withCredentials: true });
      setDeptStatusRows(res.data || []);
    } catch (err) {
      setDeptStatusError(err?.response?.data?.error || err?.message || 'Could not load department status.');
    } finally {
      setDeptStatusLoading(false);
    }
  };

  // Check if we're in overview mode (no tender_id parameter)
  const isOverviewMode = !tenderId;
  const disableCellEditing = isOverviewMode || isViewer;
  const canEditManualDepartments = Boolean(
    userType?.isAdmin && !isViewer
  );
  const canEditTender = Boolean(userType?.isAdmin && !isViewer);
  const canViewTenderInfo = Boolean(userType?.isAdmin || isViewer);

  useEffect(() => {
    const fetchManualDepartments = async () => {
      if (!tenderId || !user || !isAuth) {
        setManualDepartments([]);
        setSelectedManualDepartments([]);
        setAllLanesManualDepartments([]);
        return;
      }

      setManualDeptLoading(true);
      setManualDeptError('');
      setManualDeptSuccess('');
      try {
        const res = await axios.get(
          API_URL_TENDER_MANUAL_DEPARTMENTS(tenderId, user),
          { withCredentials: true }
        );
        const {
          departments = [],
          selected_department_ids = [],
          all_lanes_department_ids = [],
        } = res.data || {};
        setManualDepartments(departments);
        setSelectedManualDepartments(selected_department_ids);
        setAllLanesManualDepartments(all_lanes_department_ids);
      } catch (err) {
        const message =
          err?.response?.data?.error ||
          err?.message ||
          'Could not load manual department selection.';
        setManualDeptError(message);
      } finally {
        setManualDeptLoading(false);
      }
    };

    fetchManualDepartments();
  }, [tenderId, user, isAuth]);

  const handleManualDepartmentChange = (event) => {
    const {
      target: { value },
    } = event;
    let selectedDepartments = typeof value === 'string' ? value.split(',') : value;
    if (selectedDepartments.includes(SELECT_ALL_VALUE)) {
      selectedDepartments =
        selectedManualDepartments.length === manualDepartments.length
          ? []
          : manualDepartments.map((dept) => dept.id);
    }
    setSelectedManualDepartments(selectedDepartments);
    setAllLanesManualDepartments((current) =>
      selectedDepartments.length > 0
        ? current.filter((departmentId) => selectedDepartments.includes(departmentId))
        : current
    );
    setManualDeptSuccess('');
  };

  const handleAllLanesDepartmentChange = (event) => {
    const {
      target: { value },
    } = event;
    const allLanesDepartmentIds =
      typeof value === 'string' ? value.split(',') : value;
    setAllLanesManualDepartments(allLanesDepartmentIds);
    setManualDeptSuccess('');
  };

  useEffect(() => {
    if (!tenderId || !canEditTender) return;
    setContactsLoading(true);
    setContactsError('');
    axios
      .get(API_URL_TENDER_DETAILS(tenderId), { withCredentials: true })
      .then((res) => {
        const tenderData = Array.isArray(res.data) ? res.data[0] : res.data;
        setCustomerContacts(tenderData?.customer_notification_emails || []);
      })
      .catch(() => setContactsError('Could not load Customer Account Manager.'))
      .finally(() => setContactsLoading(false));
  }, [tenderId, canEditTender]);

  const handleOpenContactsDialog = () => {
    setContactsEditing([...customerContacts]);
    setNewContactEmail('');
    setContactsSaveError('');
    setContactsDialogOpen(true);
  };

  const handleAddContact = () => {
    const email = newContactEmail.trim();
    if (!email || contactsEditing.includes(email)) return;
    setContactsEditing((prev) => [...prev, email]);
    setNewContactEmail('');
  };

  const handleRemoveContact = (email) => {
    setContactsEditing((prev) => prev.filter((e) => e !== email));
  };

  const handleSaveContacts = async () => {
    setContactsSaving(true);
    setContactsSaveError('');
    try {
      await axios.post(
        `${API_URL_EDIT}tender`,
        { [tenderId]: { customer_notification_emails: contactsEditing } },
        { withCredentials: true }
      );
      setCustomerContacts(contactsEditing);
      setContactsDialogOpen(false);
    } catch (err) {
      setContactsSaveError(
        err?.response?.data?.message || err?.message || 'Could not save contacts.'
      );
    } finally {
      setContactsSaving(false);
    }
  };

  const handleSaveManualDepartments = async () => {
    if (!tenderId || !user) return;
    setManualDeptSaving(true);
    setManualDeptError('');
    setManualDeptSuccess('');
    try {
      const res = await axios.post(
        API_URL_TENDER_MANUAL_DEPARTMENTS(tenderId, user),
        {
          selected_departments: selectedManualDepartments,
          all_lanes_department_ids: allLanesManualDepartments,
        },
        { withCredentials: true }
      );
      const {
        departments = [],
        selected_department_ids = [],
        all_lanes_department_ids = [],
      } = res.data || {};
      setManualDepartments(departments);
      setSelectedManualDepartments(selected_department_ids);
      setAllLanesManualDepartments(all_lanes_department_ids);
      setManualDeptSuccess('Manual department access updated.');
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.message ||
        'Could not save manual department selection.';
      setManualDeptError(message);
    } finally {
      setManualDeptSaving(false);
    }
  };

  return (
    <Box>
      <Map></Map>
        {isAuth && tenderId && (
          <Box sx={{ mt: 3, mb: 2 }}>

            {manualDeptError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {manualDeptError}
          </Alert>
            )}
            {manualDeptSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {manualDeptSuccess}
          </Alert>
            )}

            <Stack direction="row" spacing={2} alignItems="center" justifyContent={"space-between"}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              sx={{ transform: "translateY(13px)"}}
              variant="outlined"
              size="small"
              color="inherit"
              style={{ alignSelf: 'start' }}
              onClick={() => {
                // Call reset on child components
                if (lanePricingRef.current?.reset) {
                  lanePricingRef.current.reset();
                } else {
                  const nextSearchParams = new URLSearchParams(searchParams);
                  nextSearchParams.delete('tender_id');
                  setSearchParams(nextSearchParams, { replace: true });
                }

                if (checksRef.current?.reset) {
                  checksRef.current.reset();
                }
              }}
            >
              ← Back
            </Button>
            {canEditTender && (
              <Button
                sx={{ transform: "translateY(13px)"}}
                variant="contained"
                size="small"
                onClick={() => navigate(`/NewTender?edit_tender_id=${tenderId}`)}
              >
                Edit Tender
              </Button>
            )}
            {canEditTender && (
              <Button
                sx={{ transform: "translateY(13px)"}}
                variant="outlined"
                size="small"
                onClick={() => navigate(`/Import?id=${tenderId}`)}
              >
                Manage Lanes
              </Button>
            )}
            {canViewTenderInfo && (
              <Button
                sx={{ transform: "translateY(13px)"}}
                variant="outlined"
                size="small"
                onClick={handleOpenDeptStatus}
              >
                Show Tender Info
              </Button>
            )}
          </Stack>
          {canEditManualDepartments &&
            <Stack direction="row" spacing={2} alignItems="flex-start">
            <FormControl sx={{ minWidth: 280, maxWidth: 420 }}>
              <InputLabel id="manual-department-select-label">Visible to Departments</InputLabel>
              <Select
            labelId="manual-department-select-label"
            id="manual-department-select"
            multiple
            value={selectedManualDepartments}
            onChange={handleManualDepartmentChange}
            input={<OutlinedInput label="Visible to Departments" />}
            renderValue={(selected) => {
              if (!selected || selected.length === 0) {
                return <em>Automatic (lane-based)</em>;
              }
              return manualDepartments
                .filter((dept) => selected.includes(dept.id))
                .map((dept) => dept.name)
                .join(', ');
            }}
            disabled={manualDeptLoading || !canEditManualDepartments}
              >
            <MenuItem disabled value="">
              <em>Select departments that should see this tender</em>
            </MenuItem>
            {manualDepartments.length > 0 && (
              <MenuItem value={SELECT_ALL_VALUE}>
                <Checkbox
                  checked={selectedManualDepartments.length === manualDepartments.length}
                  indeterminate={
                    selectedManualDepartments.length > 0 &&
                    selectedManualDepartments.length < manualDepartments.length
                  }
                />
                <ListItemText primary="Select all" />
              </MenuItem>
            )}
            {manualDepartments.map((dept) => (
              <MenuItem key={dept.id} value={dept.id}>
                <Checkbox checked={selectedManualDepartments.indexOf(dept.id) > -1} />
                <ListItemText primary={dept.name} />
              </MenuItem>
            ))}
              </Select>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            {canEditManualDepartments
              ? 'Leave empty to allow automatic routing. Select to restrict visibility.'
              : 'Viewing only. Contact an admin if you need changes.'}
              </Typography>
            </FormControl>
            <FormControl sx={{ minWidth: 280, maxWidth: 420 }}>
              <InputLabel id="all-lanes-department-select-label">
                Lane access
              </InputLabel>
              <Select
                labelId="all-lanes-department-select-label"
                id="all-lanes-department-select"
                multiple
                value={allLanesManualDepartments}
                onChange={handleAllLanesDepartmentChange}
                input={<OutlinedInput label="Lane access" />}
                renderValue={(selected) => {
                  if (!selected || selected.length === 0) {
                    return <em>No departments</em>;
                  }
                  return manualDepartments
                    .filter((dept) => selected.includes(dept.id))
                    .map((dept) => dept.name)
                    .join(', ');
                }}
                disabled={manualDeptLoading || !canEditManualDepartments}
              >
                {manualDepartments
                  .filter((dept) =>
                    selectedManualDepartments.length > 0
                      ? selectedManualDepartments.includes(dept.id)
                      : true
                  )
                  .map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      <Checkbox
                        checked={allLanesManualDepartments.includes(dept.id)}
                      />
                      <ListItemText primary={dept.name} />
                    </MenuItem>
                  ))}
              </Select>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Selected departments can access every lane.
              </Typography>
            </FormControl>

            <Button
              sx={{transform: "translateY(10px)"}}
              variant="contained"
              onClick={handleSaveManualDepartments}
              disabled={
            manualDeptSaving ||
            manualDeptLoading ||
            !canEditManualDepartments ||
            !tenderId
              }
            >
              {manualDeptSaving ? 'Saving…' : 'Save'}
            </Button>
            {manualDeptLoading && <CircularProgress size={24} />}
          </Stack>
          }
            </Stack>
          </Box>
        )}
        {isAuth && userType.isChecks && (
          <Checks
            ref={checksRef}
            user={user}
            tender_input={API_URL_TENDERS}
            disableCellEditing={disableCellEditing}
          ></Checks>
        )}
        {isAuth && isViewer && (
          <LanePricing
            ref={lanePricingRef}
            price_type={'final'}
            price_type_r2={'round2'}
            price_type_r3={'round3'}
            check_type={'final_pricing'}
            pricing_label={'Final Prices'}
            user={user}
            tender_input={API_URL_TENDERS}
            disableCellEditing={true}
          ></LanePricing>
        )}
        {isAuth && (userType.isRegular || userType.isStaff) && (
          <LanePricing
            ref={lanePricingRef}
            price_type={'regional'}
            check_type={'regional_pricing'}
            pricing_label={'Regional Prices Completed'}
            user={user}
            tender_input={API_URL_TENDERS}
            disableCellEditing={disableCellEditing}
          ></LanePricing>
        )}
        {userType.isAdmin && (
          <LanePricing
            ref={lanePricingRef}
            price_type={'final'}
            price_type_r2={'round2'}
            price_type_r3={'round3'}
            check_type={'final_pricing'}
            pricing_label={'Final Prices Completed'}
            user={user}
            tender_input={API_URL_TENDERS}
            disableCellEditing={disableCellEditing}
            customerContacts={customerContacts}
            contactsLoading={contactsLoading}
            contactsError={contactsError}
            onEditContacts={handleOpenContactsDialog}
          ></LanePricing>
        )}

      {/* Customer Contacts Edit Dialog */}
      <Dialog open={contactsDialogOpen} onClose={() => setContactsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Customer Account Manager</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These contacts appear as reference information in internal notification emails. No emails are sent directly to these addresses.
          </Typography>
          {contactsSaveError && (
            <Alert severity="error" sx={{ mb: 2 }}>{contactsSaveError}</Alert>
          )}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            {contactsEditing.map((email) => (
              <Chip
                key={email}
                label={email}
                onDelete={() => handleRemoveContact(email)}
                size="small"
              />
            ))}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              label="Add email"
              type="email"
              value={newContactEmail}
              onChange={(e) => setNewContactEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddContact(); } }}
              fullWidth
            />
            <Button
              variant="outlined"
              onClick={handleAddContact}
              disabled={!newContactEmail.trim()}
            >
              Add
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactsDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveContacts} disabled={contactsSaving}>
            {contactsSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Management Section - Show when a tender is selected */}
      {!isLoading && isAuth && userType && tenderId && (
        <Box sx={{ mt: 4, mb: 2 }} key={`documents-${tenderId}`}>
          <Divider sx={{ mb: 2 }} />
          <TenderDocuments key={tenderId} tenderId={tenderId} username={user} userType={userType} isViewer={isViewer} />
        </Box>
      )}

      {/* Department Status Popup */}
      <Dialog open={deptStatusOpen} onClose={() => setDeptStatusOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Department Status</DialogTitle>
        <DialogContent>
          {deptStatusLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {deptStatusError && (
            <Alert severity="error" sx={{ mb: 2 }}>{deptStatusError}</Alert>
          )}
          {!deptStatusLoading && !deptStatusError && deptStatusRows.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No departments have been assigned to this tender yet.
            </Typography>
          )}
          {!deptStatusLoading && deptStatusRows.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Department</strong></TableCell>
                    <TableCell align="center"><strong>Viewed</strong></TableCell>
                    <TableCell align="center"><strong>Declined</strong></TableCell>
                    <TableCell><strong>Decline Reason</strong></TableCell>
                    <TableCell align="center"><strong>Document Uploaded</strong></TableCell>
                    <TableCell align="center"><strong>Prices Submitted</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deptStatusRows.map((row) => (
                    <TableRow key={row.department}>
                      <TableCell>{row.department}</TableCell>
                      <TableCell align="center">
                        {row.viewed
                          ? <CheckCircleOutlineIcon color="success" fontSize="small" />
                          : <RemoveCircleOutlineIcon color="disabled" fontSize="small" />}
                      </TableCell>
                      <TableCell align="center">
                        {row.declined
                          ? <CheckCircleOutlineIcon color="error" fontSize="small" />
                          : <RemoveCircleOutlineIcon color="disabled" fontSize="small" />}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 250, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {row.declined ? row.decline_reason : '—'}
                      </TableCell>
                      <TableCell align="center">
                        {row.document_uploaded
                          ? <CheckCircleOutlineIcon color="success" fontSize="small" />
                          : <RemoveCircleOutlineIcon color="disabled" fontSize="small" />}
                      </TableCell>
                      <TableCell align="center">
                        {row.prices_submitted
                          ? <CheckCircleOutlineIcon color="success" fontSize="small" />
                          : <RemoveCircleOutlineIcon color="disabled" fontSize="small" />}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          {deptStatusRows.length > 0 && (
            <Button onClick={() => exportDepartmentStatusToExcel(tenderId, deptStatusRows)}>
              Export
            </Button>
          )}
          <Button onClick={() => setDeptStatusOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
export default AdvanceTender;
